import React from 'react';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Card from 'react-bootstrap/Card';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import LoadingSpinner from '../common/LoadingSpinner';
import type { UserDetail } from '../../services/types';
import { formatGermanDateTime, orEmptyValue } from '../../utils/dateFormat';
import { formatAuthType, formatUserAddress, formatUserDisplayName } from './userDisplay';

/** Props der UserDetailPanel-Komponente. */
export interface UserDetailPanelProps {
  /** Stammdaten oder null (kein Benutzer gewaehlt, laedt noch, Fehler). */
  readonly user: UserDetail | null;
  /** true, waehrend die Stammdaten geladen werden. */
  readonly loading: boolean;
  /** Deutsche Fehlermeldung oder null. */
  readonly error: string | null;
  /** true, waehrend eine Aenderung am Office-Export gesendet wird. */
  readonly officeExportSaving: boolean;
  /** Deutsche Fehlermeldung des Office-Export-Schalters oder null. */
  readonly officeExportError: string | null;
  /** Wird beim Umschalten des Office-Export-Schalters aufgerufen. */
  readonly onOfficeExportChange: (enabled: boolean) => void;
}

/** Props einer einzelnen Stammdaten-Zeile. */
interface DetailFieldProps {
  /** HTML-ID des Wert-Elements (das Label erhaelt `<fieldId>-label`). */
  readonly fieldId: string;
  /** Deutsches Label der Zeile. */
  readonly label: string;
  /** Anzuzeigender Wert. */
  readonly children: React.ReactNode;
}

/** Eine Zeile der Stammdaten (Label links, Wert rechts). */
function DetailField({ fieldId, label, children }: DetailFieldProps): React.JSX.Element {
  return (
    <>
      <dt id={`${fieldId}-label`} className="col-5 col-sm-4 small text-muted fw-normal">
        {label}
      </dt>
      <dd id={fieldId} className="col-7 col-sm-8 mb-2 text-break">
        {children}
      </dd>
    </>
  );
}

/**
 * Stammdaten des gewaehlten Benutzers (rechte Spalte des
 * Master-Detail-Layouts).
 *
 * Zustaende: Platzhalter ohne Auswahl, Spinner waehrend des Ladens,
 * Alert im Fehlerfall, sonst die Stammdaten. Fehlende Einzelwerte
 * erscheinen als Gedankenstrich.
 */
function UserDetailPanel({
  user,
  loading,
  error,
  officeExportSaving,
  officeExportError,
  onOfficeExportChange,
}: UserDetailPanelProps): React.JSX.Element {
  return (
    <Card id="user-detail-card" className="shadow-sm">
      <Card.Body id="user-detail-card-body">
        <Card.Title as="h2" id="user-detail-title" className="h6 mb-3">
          Stammdaten
        </Card.Title>

        {loading && (
          <div id="user-detail-loading-container" className="d-flex justify-content-center py-4">
            <LoadingSpinner id="user-detail-loading" />
          </div>
        )}

        {!loading && error !== null && (
          <Alert id="user-detail-error" variant="danger" className="mb-0">
            {error}
          </Alert>
        )}

        {!loading && error === null && user === null && (
          <p id="user-detail-empty" className="text-muted mb-0">
            Bitte einen Benutzer auswählen.
          </p>
        )}

        {!loading && error === null && user !== null && (
          <dl id="user-detail" className="row mb-0">
            <DetailField fieldId="user-detail-name" label="Name / Firma">
              {formatUserDisplayName(user)}
              {user.isLegalPerson && (
                <Badge id="user-detail-legal-person" bg="secondary" className="ms-2">
                  Firma
                </Badge>
              )}
            </DetailField>
            <DetailField fieldId="user-detail-username" label="Anmeldename">
              {orEmptyValue(user.username)}
            </DetailField>
            <DetailField fieldId="user-detail-email" label="E-Mail">
              {orEmptyValue(user.email)}
              {user.emailVerified && (
                <Badge id="user-detail-email-verified" bg="success" className="ms-2">
                  Bestätigt
                </Badge>
              )}
            </DetailField>
            <DetailField fieldId="user-detail-address" label="Anschrift">
              {formatUserAddress(user)}
            </DetailField>
            <DetailField fieldId="user-detail-phone" label="Telefon">
              {orEmptyValue(user.phone)}
            </DetailField>
            <DetailField fieldId="user-detail-authtype" label="Anmeldeart">
              {formatAuthType(user.authType)}
            </DetailField>
            <DetailField fieldId="user-detail-status" label="Status">
              <span id="user-detail-status-badges" className="d-inline-flex flex-wrap gap-1">
                <Badge id="user-detail-status-active" bg={user.active ? 'success' : 'secondary'}>
                  {user.active ? 'Aktiv' : 'Inaktiv'}
                </Badge>
                <Badge
                  id="user-detail-status-subscription"
                  bg={user.subscriptionActive ? 'info' : 'light'}
                  text="dark"
                >
                  {user.subscriptionActive ? 'Abo aktiv' : 'Kein Abo'}
                </Badge>
              </span>
            </DetailField>
            <DetailField fieldId="user-detail-office-export" label="Office-Export">
              <span id="user-detail-office-export-controls" className="d-inline-flex align-items-center gap-2">
                {/*
                  Form.Check spreadet seine `id`-Prop NICHT auf das
                  Wrapper-div, sobald Kind-Elemente (Input/Label) uebergeben
                  werden — daher hier ein eigenes div mit den Bootstrap-
                  Switch-Klassen statt `<Form.Check id=... >...</Form.Check>`.
                  Die ID auf dem Input bleibt der stabile E2E-Selektor.
                */}
                <div id="user-detail-office-export-switch-wrapper" className="form-check form-switch">
                  <Form.Check.Input
                    id="user-detail-office-export-switch"
                    type="checkbox"
                    checked={user.officeExportEnabled}
                    disabled={officeExportSaving}
                    onChange={(event) => onOfficeExportChange(event.target.checked)}
                  />
                  <Form.Check.Label id="user-detail-office-export-switch-label" htmlFor="user-detail-office-export-switch">
                    {user.officeExportEnabled ? 'Aktiviert' : 'Deaktiviert'}
                  </Form.Check.Label>
                </div>
                {officeExportSaving && (
                  <Spinner id="user-detail-office-export-saving" animation="border" size="sm" />
                )}
              </span>
              {officeExportError !== null && (
                <Alert
                  id="user-detail-office-export-error"
                  variant="danger"
                  className="mt-2 mb-0 py-1 small"
                >
                  {officeExportError}
                </Alert>
              )}
            </DetailField>
            <DetailField fieldId="user-detail-created" label="Registriert am">
              {formatGermanDateTime(user.createdAt)}
            </DetailField>
            <DetailField fieldId="user-detail-updated" label="Zuletzt geändert">
              {formatGermanDateTime(user.updatedAt)}
            </DetailField>
          </dl>
        )}
      </Card.Body>
    </Card>
  );
}

export default UserDetailPanel;
